"use client";

import { createContext, Fragment, useContext, type ReactNode } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  hasBreadcrumbOverrides,
  resolveBreadcrumbs,
  type BreadcrumbOverride,
} from "@/lib/seo/breadcrumbs";
import { cn } from "@/utils";

const BreadcrumbOverrideContext = createContext<
  BreadcrumbOverride[] | null | undefined
>(undefined);

export function BreadcrumbOverrideProvider({
  overrides,
  children,
}: {
  overrides?: BreadcrumbOverride[] | null;
  children: ReactNode;
}) {
  return (
    <BreadcrumbOverrideContext.Provider value={overrides}>
      {children}
    </BreadcrumbOverrideContext.Provider>
  );
}

function crumbPath(url: string): string {
  if (!url) return "";
  try {
    const path = new URL(url).pathname;
    if (path.length > 1 && path.endsWith("/")) return path.slice(0, -1);
    return path || "/";
  } catch {
    return url.startsWith("/") ? url : `/${url}`;
  }
}

function normalizePath(path: string): string {
  if (path.length > 1 && path.endsWith("/")) return path.slice(0, -1);
  return path || "/";
}

const currentCrumbClass =
  "font-normal text-white truncate max-w-[200px] md:max-w-[400px]";

export default function Breadcrumbs({
  className,
  tone = "default",
}: {
  className?: string;
  /** Lighter parent crumbs for bright pixel backgrounds. */
  tone?: "default" | "light";
}) {
  const pathname = usePathname();
  const overrides = useContext(BreadcrumbOverrideContext);
  if (!pathname || pathname === "/") return null;

  const usingOverride = hasBreadcrumbOverrides(overrides);
  const trail = resolveBreadcrumbs({ pagePath: pathname, overrides });
  if (trail.length < 2) return null;

  const currentPath = normalizePath(pathname);
  const muted = tone === "light" ? "text-white/70" : "text-gray-400";
  const separator = tone === "light" ? "text-white/50" : "text-gray-500";

  return (
    <Breadcrumb className={cn("col-span-full w-full", className)}>
      <BreadcrumbList className={cn("gap-1.5 text-sm sm:gap-1.5", muted)}>
        {trail.map((item, index) => {
          const path = crumbPath(item.url);
          const isLast = index === trail.length - 1;
          const isCurrent =
            isLast || (!usingOverride && !!path && path === currentPath);

          return (
            <Fragment key={`${index}-${item.name}`}>
              <BreadcrumbItem>
                {isCurrent ? (
                  <BreadcrumbPage className={currentCrumbClass}>
                    {item.name}
                  </BreadcrumbPage>
                ) : item.url ? (
                  <BreadcrumbLink
                    asChild
                    className={cn(
                      muted,
                      "transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 rounded-sm"
                    )}
                  >
                    <Link href={path}>{item.name}</Link>
                  </BreadcrumbLink>
                ) : (
                  <span className={cn("truncate", muted)}>{item.name}</span>
                )}
              </BreadcrumbItem>
              {!isLast && (
                <BreadcrumbSeparator className={separator} />
              )}
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
